/**
 * Aligns to IndicatorName in https://sourcegraph.prod.tableautools.com/teams/near/-/blob/workgroup/src/silos/tableau-server/interfaces/tab-domain-data-quality-interfaces/src/com/tableausoftware/domain/dataquality/IDataQualityIndicator.java
 */
export type UserSpecifiedIndicatorName = 'certified' | 'warning' | 'deprecated' | 'stale' | 'maintenance' | 'sensitive_data';

export function isUserSpecifiedIndicatorName(value: string): value is UserSpecifiedIndicatorName {
  return ['certified', 'warning', 'deprecated', 'stale', 'maintenance', 'sensitive_data'].includes(value);
}

/*
 ToDo : Story 1424438
 Remove tableWithCrass from CertificationContentTypes and isCertifiable
*/
export type CertificationContentTypes = {
  readonly category: 'certification';
  readonly contentTypes: 'datasource' | 'database' | 'table' | 'tableWithCrass' | 'virtualConnection' | 'virtualConnectionTable' | 'view';
}

export function isCertifiable(value: string): value is CertificationContentTypes['contentTypes'] {
  return ['datasource', 'database', 'table', 'tableWithCrass', 'virtualConnection', 'virtualConnectionTable', 'view'].includes(value);
}

export type WarningContentTypes = {
  readonly category: 'warning';
  readonly contentTypes: 'datasource' | 'database' | 'table' | 'virtualConnection' | 'virtualConnectionTable' | 'flow' | 'column';
}

export function isWarnable(value: string): value is WarningContentTypes['contentTypes'] {
  return ['datasource', 'database', 'table', 'virtualConnection', 'virtualConnectionTable', 'flow', 'column'].includes(value);
}


export type SensitiveDataLabelTypes = {
  readonly category: 'sensitivity';
  readonly contentTypes: 'datasource' | 'database' | 'table' | 'virtualConnection' | 'virtualConnectionTable' | 'flow' | 'column';
}

export function isSensitivity(value: string): value is SensitiveDataLabelTypes['contentTypes'] {
  return ['datasource', 'database', 'table', 'virtualConnection', 'virtualConnectionTable', 'flow', 'column'].includes(value);
}

export function isLabelContentType(value: string): value is LabelContentItem['contentType'] {
  return isCertifiable(value) || isWarnable(value) || isSensitivity(value);
}

export type LabelContentItem = {
  readonly contentName?: string;
  readonly contentId: string;
  readonly contentType: CertificationContentTypes['contentTypes'] | WarningContentTypes['contentTypes'] | SensitiveDataLabelTypes['contentTypes'];
  readonly contentIcon?: JSX.Element;
  readonly contentIconTitle?: string;
}

export type Label = {
  readonly luid: string;
  readonly message: string;
  readonly active: boolean;
  readonly elevated: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly value: string;
  readonly category: CertificationContentTypes['category'] | WarningContentTypes['category'] | SensitiveDataLabelTypes['category'];
  readonly owner?: LabelOwner;
  readonly content?: LabelContent;
}

export type LabelOwner = {
  readonly ownerName: string;
  readonly ownerHref?: string;
}

export type LabelContent = {
  readonly contentType: string;
  readonly contentName: string;
  readonly contentHref: string;
}

export type RestLabelResponse = {
  readonly site: { readonly id: string };
  readonly owner: { readonly id: string };
  readonly id: string;
  readonly userDisplayName: string;
  readonly contentId: string;
  readonly contentType: string;
  readonly message: string;
  readonly value: string;
  readonly category: string;
  readonly active: boolean;
  readonly elevated: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export type RestLabelResponseWrapper = {
  readonly labelList: {
    readonly label: ReadonlyArray<RestLabelResponse>;
  };
};

export type RestLabelValueResponse = {
  readonly name: string;
  readonly category: string;
  readonly description: string;
  readonly internal: boolean;
  readonly site: { readonly id: string }
}

export type RestLabelValueResponseWrapper = {
  readonly labelValueList: {
    readonly labelValue : ReadonlyArray<RestLabelValueResponse>;
  };
}
